/** Ручное связывание потолочного светильника с настенным электроприбором. */
import { useEffect, useRef, useState } from "react";
import type { PointerEvent } from "react";
import { getCatalogItem, isLight, isSwitch } from "@/shared/catalog";
import { useProjectStore } from "@/shared/store";
import { addLightingLink } from "../logic";
import { SelectionKind, Tool, useEditorStore } from "@/widgets/editor";

export const useDrawLightingConnection = () => {
	const tool = useEditorStore((s) => s.tool);
	const style = useEditorStore((s) => s.lightingConnectionStyle);
	const reset = useEditorStore((s) => s.drawResetToken);
	const setSelection = useEditorStore((s) => s.setSelection);
	const [from, setFrom] = useState<string | null>(null);
	const fromRef = useRef<string | null>(null);
	const active = tool === Tool.LightingConnection;

	const cancel = () => {
		fromRef.current = null;
		setFrom(null);
	};

	useEffect(() => {
		cancel();
		// oxlint-disable-next-line react-hooks/exhaustive-deps
	}, [active, reset]);

	const handleFixturePointerDown = (event: PointerEvent<Element>, id: string): boolean => {
		if (!active || event.button !== 0) return false;
		event.stopPropagation();
		const electrical = useProjectStore.getState().project.layers.electrical;
		if (!fromRef.current) {
			const fixture = electrical.fixtures[id];
			if (!fixture || !isSwitch(getCatalogItem(fixture.ref))) return true;
			fromRef.current = id;
			setFrom(id);
			return true;
		}
		if (fromRef.current === id) return true;
		const target = electrical.items[id] ?? electrical.fixtures[id];
		if (!target || !isLight(getCatalogItem(target.ref))) return true;
		const connectionId = useProjectStore
			.getState()
			.recordEdit(() =>
				addLightingLink(
					electrical,
					useProjectStore.getState().project.layers.wiring.items,
					fromRef.current as string,
					id,
					style,
				),
			);
		cancel();
		if (connectionId) setSelection({ kind: SelectionKind.LightingConnection, id: connectionId });
		return true;
	};

	return {
		active,
		from,
		cancel,
		handleFixturePointerDown,
		hint: active
			? from
				? "Кликните по светильнику, который включает этот выключатель"
				: "Кликните по выключателю, от которого провести линию"
			: null,
	};
};
